/**
 * The lock on your own account, drawn beside the heading.
 *
 * Decorative and `aria-hidden` — every fact it suggests is already written in
 * the chips beside it. Deliberately not the campus, the climb, the staff room or
 * the notice board: this page is about *the key to one account*, so it draws the
 * shield with the keyhole in it and the key on its way to turning.
 *
 * Built from `fill-*` utilities rather than gradient stops so it re-tints with
 * the theme (CLAUDE.md §7), and animated only with keyframes the product already
 * ships, every one of which is held still under `prefers-reduced-motion`.
 */
export function SecurityGraphic() {
  return (
    <svg viewBox="0 0 200 140" className="h-auto w-full" role="presentation" aria-hidden>
      {/* the light behind it */}
      <circle cx="94" cy="66" r="52" className="fill-blue-200/60 dark:fill-blue-500/10" />
      <circle
        cx="94"
        cy="66"
        r="34"
        className="fill-sky-200/55 animate-glow dark:fill-sky-500/10"
      />

      {/* the shield, and the plate inside it */}
      <path
        d="M94 14 L142 32 v38 c0 26 -20 44 -48 54 c-28 -10 -48 -28 -48 -54 V32 Z"
        className="fill-blue-600 dark:fill-blue-500"
      />
      <path
        d="M94 26 L130 39 v31 c0 20 -15 34 -36 42 c-21 -8 -36 -22 -36 -42 V39 Z"
        className="fill-blue-100 dark:fill-slate-800"
      />

      {/* the keyhole — the one thing this page changes */}
      <circle cx="94" cy="66" r="11" className="fill-blue-600 dark:fill-blue-400" />
      <path d="M89 72 L99 72 L96 88 L92 88 Z" className="fill-blue-600 dark:fill-blue-400" />
      <circle cx="94" cy="66" r="4.5" className="fill-blue-100 dark:fill-slate-800" />

      {/* the tick, breathing — the account is in order once this is done */}
      <g className="animate-glow">
        <circle cx="128" cy="96" r="13" className="fill-emerald-500" />
        <path
          d="M122 96 L126 100 L134 92"
          className="fill-none stroke-white"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* the key, drifting up to the lock */}
      <g className="animate-float-slow">
        <circle cx="30" cy="52" r="9" className="fill-amber" />
        <circle cx="30" cy="52" r="3.5" className="fill-blue-100 dark:fill-slate-800" />
        <rect x="37" y="49.5" width="26" height="5" rx="2.5" className="fill-amber" />
        <rect x="52" y="54" width="4" height="7" rx="2" className="fill-amber" />
        <rect x="59" y="54" width="4" height="5" rx="2" className="fill-amber" />
      </g>

      {/* three dots standing in for the characters being typed */}
      <g className="animate-float-slow delay-500">
        <circle cx="162" cy="42" r="4" className="fill-blue-400 dark:fill-blue-300" />
        <circle cx="174" cy="42" r="4" className="fill-blue-400 dark:fill-blue-300" />
        <circle cx="186" cy="42" r="4" className="fill-blue-400 dark:fill-blue-300" />
      </g>

      {/* sparks around the shield */}
      <circle cx="20" cy="94" r="3" className="fill-amber animate-sparkle" />
      <circle cx="170" cy="110" r="2.5" className="fill-emerald-400 animate-sparkle delay-500" />
      <circle cx="18" cy="24" r="2" className="fill-blue-400 animate-sparkle delay-200" />
      <circle cx="180" cy="76" r="2" className="fill-violet-400 animate-sparkle delay-700" />
    </svg>
  );
}
